import { Box } from '@mui/material';
import { OrderScenario } from 'components/order/model/Order';
import { useDeliveryScenarioGuard } from 'components/delivery/hooks/useDeliveryScenarioGuard';
import { UserAddress } from 'components/user/model/User';
import { REDUCED_CONTENT_MAX_WIDTH } from 'config/constants';
import { useLocalHistory } from 'lib/useLocalHistory';
import { ROUTES } from 'pages/routes';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeRouteScenario } from 'src/utils/route';
import { AddressBook } from 'components/delivery/AddressBook';
import { useDeliveryState } from 'components/delivery/hooks/useDeliveryState';
import { resetBasket } from 'store/basket/basketSlice';
import { useAuth } from 'lib/useAuth';
import { useParams } from 'react-router';
import { getShouldUpdateGuestSession, resetShouldUpdateGuestSession } from 'components/user/localAuth';
import { createGuestSession } from 'store/auth/authSlice';
import { useAppDispatch } from 'store/hooks';

export const DeliveryPage: React.FC = () => {
    useDeliveryScenarioGuard();
    const dispatch = useAppDispatch();
    const { getParsedPath, push } = useLocalHistory();
    const { merchantId } = useParams();
    const { isGuest } = useAuth();
    const { t } = useTranslation();
    const { setSelectedAddress } = useDeliveryState(false);
    const handleAddressClick = React.useCallback(
        (address: UserAddress) => {
            dispatch(resetBasket());
            setSelectedAddress(address._id);
            const route = getParsedPath(ROUTES.DELIVERY.LOCATIONS, {
                scenario: makeRouteScenario(OrderScenario.DELIVERY)
            });
            push(route);
        },
        [dispatch, getParsedPath, push, setSelectedAddress]
    );
    // If the user is a guest, and the merchantId is defined, check if we need to update the guest session
    // we don't want the guest user to have more than one order
    React.useEffect(() => {
        if (isGuest && merchantId) {
            const shouldUpdateGuestSession = getShouldUpdateGuestSession(merchantId);
            if (shouldUpdateGuestSession) {
                dispatch(createGuestSession())
                    .unwrap()
                    .then(() => {
                        resetShouldUpdateGuestSession(merchantId);
                    });
            }
        }
    }, [dispatch, isGuest, merchantId]);
    return (
        <Box
            maxWidth={REDUCED_CONTENT_MAX_WIDTH}
            marginX="auto"
            minHeight="100%"
            sx={{ backgroundColor: 'background.paper' }}
        >
            <AddressBook
                disableBack
                onAddressClick={handleAddressClick}
                hideUserAvatar={false}
                title={t('DELIVERY_ADDRESS_TITLE')}
            />
        </Box>
    );
};
